import {
  useMutation,
  useQuery,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import compact from 'lodash/compact';
import {
  AppointmentReminderCallResponseDTO,
  AppointmentConfirmationRequestDTO,
  CommuniqueVisibilityFlagsResponseDTO,
  ConfirmAppointmentCommuniqueResponseDTO,
  ConsentFormsCommuniqueDTO,
  GetApiCHGetFavoriteInsurancesByOrgParams,
  GetApiCHSearchInsuranceByOrgParams,
  GetApiCommunicationGetAppointmentCommuniqueByTokenParams,
  GetApiCommunicationGetIntakeFlagsByTokenParams,
  GetApiOrganizationGetOrgByPatientIdParams,
  FormFieldAssignee,
  FormWorkflowCompletionRequestDTO,
  FormWorkflowWithTransitionSuccessMessageResponseDTO,
  IntakeAppTokenByDemographicsRequest,
  IntakeAppTokenByLinkRequest,
  IntakeAppTokenResponse,
  InsuranceSettingsDTO,
  InsuranceAttachmentDTO,
  None,
  OrganizationByIntakeRequestDTO,
  OrganizationByIntakeResponseDTO,
  OrganizationDTO,
  PatientDOBDTO,
  PayerEntryDTO,
  PostApiAppointmentReminderAgentCallForAppointmentReminderParams,
  SettingsDTO,
  ModifyAppointmentStatusResponseDTO,
  DosespotConsentByPatientDTO,
  GetApiERxPharmacySearchParams,
  GetApiERxMedicationSearchParams,
  GetApiERxDrugAllergySearchParams,
  CommuniqueByPatientInfoRequestDTO,
  FormsRenderingMetadataResponseDTO,
  OrgBannerPictureByTokenRequestDTO,
  PatientDTO,
  ISyncPatientResult,
  PatientMultiMediaResponseDTO,
  PatientMultiMediaUploadDTO,
  AppointmentDetailsDTO,
  FormsResponseDTO,
  ReasonForVisitDTO,
  PatientPharmacyAllergyMedicationRequestDTO,
  MultiMediaDeleteDTO,
  AddressDTO,
  ERxPharmacySearchResponseDTO,
  ERxMedicationSearchResultDTO,
  ERxDrugAllergySearchResultDTO,
  SquareInvoiceSummaryResponseDTO,
  PatientInsurancesRequestDTO,
  SyncInsuranceAttachmentsRequestDTO,
  UploadAndAssociateInsuranceCardRequestDTO,
  NPISearchItemDTO,
  RTMAnswers,
  RTMEnrollmentInfoResponse,
  RTMPatientStatusResponseDTO,
  RTMQuestionsResponse,
  TempoAnswers,
  TempoQuestionsResponse,
  TempoSubmitAnswerResponseDto,
  PatientPCCItemsDTO,
  EnsureAppointmentFormWorkflowResponseDTO,
  PostApiCommunicationEnsureFormWorkflowForAppointmentCommuniqueParams,
} from 'artifacts/dtos';
import type { GetApiIntakeAppProviderSearchExternalProviderParams } from 'artifacts/dtos/getApiIntakeAppProviderSearchExternalProviderParams';
import { MultiMediaResponseDTO } from 'artifacts/dtos/multiMediaResponseDTO';
import { PatientInfoCommuniqueDTO } from 'artifacts/dtos/patientInfoCommuniqueDTO';
import { MultimediaType } from 'utils/constants';
import isNil from 'lodash/isNil';
import {
  getApiCommunicationGetPatientInfoCommuniqueByTokenToken,
  getApiMultimediaGetOrgBannerPictureByPatientIdPatientId,
  postApiEncounterSetAppointmentConfirmation,
  postApiIntakeAppAuthGetBannerPictureByToken,
  postApiIntakeAppAuthGetTokenByDemographics,
  postApiIntakeAppAuthGetTokenByLink,
  postApiOrganizationGetOrgByIpadBaseIntakeUrl,
  getApiSettingsGetIntakeFormSettings,
  getApiCommunicationGetAppointmentCommuniqueByToken,
} from 'artifacts/endpointsM2M';
import {
  getApiCommunicationGetConsentFormCommuniqueByTokenToken,
  getApiOrganizationGetOrgByPatientId,
  postApiAppointmentReminderAgentCallForAppointmentReminder,
  getApiEncounterGetAppointmentDetailsByAppointmentIdAppointmentId,
  postApiMultimediaDeleteMultiMedia,
  postApiMultimediaPostPatientMultiMedia,
  getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
  getApiMultimediaGetBillingMultiMediaByIdForPatientBillingMultiMediaId,
  getApiMultimediaGetPatientMultimediaByTypePatientIdMultimediaType,
  getApiBillingGetInvoiceSummary,
  getApiAddressGetGeoCodeByAddress,
  getApiPatientGetAgreementFormsForPatientPatientId,
  postApiPatientAddPatientMedicalHistory,
  postApiPatientAddPatientPharmacyAllergyAndMedication,
  postApiPatientAddReasonForVisit,
  postApiPatientVerifyPatientDOBById,
  postApiPatientSubmitDosespotConsentForm,
  getApiCHGetFavoriteInsurancesByOrg,
  getApiCHSearchInsuranceByOrg,
  getApiCommunicationGetIntakeFlagsByToken,
  getApiSettingsGetInsuranceSettings,
  getApiERxPharmacySearch,
  getApiERxMedicationSearch,
  getApiERxDrugAllergySearch,
  postApiCommunicationGetOrCreateCommuniqueByPatientInfoAndUpdateAppointment,
  postApiCommunicationEnsureFormWorkflowForAppointmentCommunique,
  getApiFormWorkflowGetRenderingMetadataFormWorkflowIdAssignee,
  getApiFormWorkflowGetWorkflowPdfsFormWorkflowId,
  postApiFormWorkflowCompletePatientStep,
  getApiPatientGetPatientByIdWithPharmacyMedicationAndAllergyId,
  getApiPatientGetSymptomsFromImmediatePastVisitPatientId,
  postApiPatientAddPatientInsurances,
  getApiPatientGetInsuranceAttachmentsInsuranceId,
  getApiPatientGetUnassociatedInsuranceCardsPatientId,
  postApiPatientSyncInsuranceAttachments,
  postApiPatientUploadAndAssociateInsuranceCardWithInsurance,
  getApiIntakeAppProviderSearchExternalProvider,
  getApiTempoTokenGetQuestions,
  postApiTempoTokenSubmitAnswers,
  getApiRTMGetStatusByPatientId,
  getApiRTMGetCheckInQuestions,
  postApiRTMSubmitCheckInAnswers,
  getApiRTMGetEnrollmentInfo,
  postApiRTMCompleteEnrollment,
  getApiPatientGetPCCItemsByPatientIdPatientId,
} from 'artifacts/endpoints';
import { isEmptyString } from 'utils/helper';
import {
  setHeaders,
} from 'utils/auth';

export function useGetOrgBannerPictureByPatientId(): UseMutationResult<
  MultiMediaResponseDTO,
  Error,
  number
  > {
  return useMutation<MultiMediaResponseDTO, Error, number>({
    mutationFn: (patientId) => getApiMultimediaGetOrgBannerPictureByPatientIdPatientId(patientId),
  });
}

export function useGetOrgBannerPictureByLink(): UseMutationResult<
  MultiMediaResponseDTO,
  Error,
  OrgBannerPictureByTokenRequestDTO
  > {
  return useMutation<MultiMediaResponseDTO, Error, OrgBannerPictureByTokenRequestDTO>({
    mutationFn: (request) => postApiIntakeAppAuthGetBannerPictureByToken(request),
  });
}

/**
 * Retrieves an intake app access token using a secure link & date of birth.
 * On success, automatically sets the token in primary axios clients.
 */
export function useGetTokenByLink(): UseMutationResult<
  IntakeAppTokenResponse, Error, IntakeAppTokenByLinkRequest> {
  return useMutation<IntakeAppTokenResponse, Error, IntakeAppTokenByLinkRequest>({
    mutationFn: (params) => postApiIntakeAppAuthGetTokenByLink(params),
    onSuccess: (data) => {
      if (!isEmptyString(data?.accessToken)) {
        setHeaders(data?.accessToken ?? '');
      }
    },
  });
}

/**
 * Retrieves an intake app access token using patient demographics
 * (firstName, lastName, dateOfBirth, phoneNumber).
 * On success, automatically sets the token in primary axios clients.
 */
export function useGetTokenByDemographics(): UseMutationResult<
  IntakeAppTokenResponse, Error, IntakeAppTokenByDemographicsRequest> {
  return useMutation<IntakeAppTokenResponse, Error, IntakeAppTokenByDemographicsRequest>({
    mutationFn: (params) => postApiIntakeAppAuthGetTokenByDemographics(params),
    onSuccess: (data) => {
      // Set auth header for both primary clients (`axiosInstance` + `axiosRequestInstance`)
      setHeaders(data.accessToken as string);
    },
  });
}

export function useGetIntakeFlagsByToken(): UseMutationResult<
  CommuniqueVisibilityFlagsResponseDTO,
  Error,
  GetApiCommunicationGetIntakeFlagsByTokenParams
  > {
  return useMutation<
  CommuniqueVisibilityFlagsResponseDTO,
  Error,
  GetApiCommunicationGetIntakeFlagsByTokenParams
  >({
    mutationFn: (params) => getApiCommunicationGetIntakeFlagsByToken(params),
  });
}

export function useGetOrCreateCommuniqueByPatientInfoAndUpdateAppointment(): UseMutationResult<
  ConfirmAppointmentCommuniqueResponseDTO,
  Error,
  CommuniqueByPatientInfoRequestDTO> {
  return useMutation<
  ConfirmAppointmentCommuniqueResponseDTO,
  Error,
  CommuniqueByPatientInfoRequestDTO
  >({
    mutationFn: async (payload) => {
      const data = await postApiCommunicationGetOrCreateCommuniqueByPatientInfoAndUpdateAppointment(
        payload,
      );
      if (!data) throw new Error('No content found with this link.');
      return data;
    },
  });
}

export function useEnsureFormWorkflowForAppointmentCommunique(): UseMutationResult<
  EnsureAppointmentFormWorkflowResponseDTO,
  Error,
  PostApiCommunicationEnsureFormWorkflowForAppointmentCommuniqueParams> {
  return useMutation<
  EnsureAppointmentFormWorkflowResponseDTO,
  Error,
  PostApiCommunicationEnsureFormWorkflowForAppointmentCommuniqueParams
  >({
    mutationFn: (params) => postApiCommunicationEnsureFormWorkflowForAppointmentCommunique(params),
  });
}

export function useGetIntakeFormSettings(enabled: boolean): UseQueryResult<SettingsDTO, Error> {
  return useQuery<SettingsDTO, Error>({
    queryKey: ['intakeFormSettings'],
    queryFn: getApiSettingsGetIntakeFormSettings,
    enabled,
  });
}

export function useGetTempoQuestions(
  token: string,
  enabled: boolean = true,
): UseQueryResult<TempoQuestionsResponse, Error> {
  return useQuery<TempoQuestionsResponse, Error>({
    queryKey: ['tempoQuestions', token],
    queryFn: () => getApiTempoTokenGetQuestions(token),
    enabled: enabled && !isEmptyString(token),
  });
}

export function useGetRTMStatusByPatientId(
  patientId?: number,
  enabled = true,
): UseQueryResult<RTMPatientStatusResponseDTO, Error> {
  return useQuery<RTMPatientStatusResponseDTO, Error>({
    queryKey: ['rtm-status', patientId],
    queryFn: () => getApiRTMGetStatusByPatientId({ patientId }),
    enabled: enabled && (patientId ?? 0) > 0,
  });
}

export function useGetRTMCheckInQuestions(
  token: string,
  enabled = true,
): UseQueryResult<RTMQuestionsResponse, Error> {
  return useQuery<RTMQuestionsResponse, Error>({
    queryKey: ['rtm-check-in-questions', token],
    queryFn: () => getApiRTMGetCheckInQuestions({ token }),
    enabled: enabled && !isEmptyString(token),
  });
}

export function useSubmitRTMCheckIn(
  token: string,
): UseMutationResult<None, Error, RTMAnswers> {
  return useMutation<None, Error, RTMAnswers>({
    mutationFn: (rtmAnswers) => postApiRTMSubmitCheckInAnswers({
      ...rtmAnswers,
      token,
    }),
  });
}

export function useGetRTMEnrollmentInfo(
  token: string,
  enabled = true,
): UseQueryResult<RTMEnrollmentInfoResponse, Error> {
  return useQuery<RTMEnrollmentInfoResponse, Error>({
    queryKey: ['rtm-enrollment-info', token],
    queryFn: () => getApiRTMGetEnrollmentInfo({ token }),
    enabled: enabled && !isEmptyString(token),
  });
}

export function useCompleteRTMEnrollment(
  token: string,
): UseMutationResult<None, Error, void> {
  return useMutation<None, Error, void>({
    mutationFn: () => postApiRTMCompleteEnrollment({ token }),
  });
}

export function usePostTempoSubmitAnswers(
  token: string,
): UseMutationResult<TempoSubmitAnswerResponseDto, Error, TempoAnswers> {
  return useMutation<TempoSubmitAnswerResponseDto, Error, TempoAnswers>({
    mutationFn: (tempoAnswers) => postApiTempoTokenSubmitAnswers(token, tempoAnswers),
  });
}

export function useGetInsuranceSettings(
  enabled: boolean = true,
): UseQueryResult<InsuranceSettingsDTO, Error> {
  return useQuery<InsuranceSettingsDTO, Error>({
    queryKey: ['insuranceSettings'],
    queryFn: getApiSettingsGetInsuranceSettings,
    enabled,
  });
}

export function useSearchInsuranceByOrg(
  params: GetApiCHSearchInsuranceByOrgParams,
  enabled: boolean = true,
): UseQueryResult<PayerEntryDTO[], Error> {
  return useQuery<PayerEntryDTO[], Error>({
    queryKey: [
      'searchInsuranceByOrg',
      params?.search,
      params?.insuranceType,
      params?.organizationId,
      params?.searchType,
    ],
    queryFn: () => getApiCHSearchInsuranceByOrg(params),
    enabled,
  });
}

export function useSearchExternalProviders(
  queryParams: GetApiIntakeAppProviderSearchExternalProviderParams,
  enabled: boolean,
): UseQueryResult<NPISearchItemDTO[], Error> {
  return useQuery<NPISearchItemDTO[], Error>({
    queryKey: ['external-provider-search', queryParams],
    queryFn: async () => {
      const response = await getApiIntakeAppProviderSearchExternalProvider({
        ...queryParams,
        npiType: queryParams.npiType ?? 'NPI-1 Individual',
      });
      return response.result ?? [];
    },
    enabled,
  });
}

export function useGetAppointmentCommuniqueByToken(
  params: GetApiCommunicationGetAppointmentCommuniqueByTokenParams,
  enabled: boolean = true,
): UseQueryResult< ConfirmAppointmentCommuniqueResponseDTO, Error> {
  return useQuery< ConfirmAppointmentCommuniqueResponseDTO, Error>({
    queryKey: ['appointmentCommuniqueByToken', params?.token],
    queryFn: () => getApiCommunicationGetAppointmentCommuniqueByToken(params),
    enabled,
  });
}

export function useSetAppointmentConfirmation(): UseMutationResult<
  ModifyAppointmentStatusResponseDTO,
  Error,
  AppointmentConfirmationRequestDTO
  > {
  return useMutation<ModifyAppointmentStatusResponseDTO, Error, AppointmentConfirmationRequestDTO>({
    mutationFn: (payload) => postApiEncounterSetAppointmentConfirmation(payload),
  });
}

export function useGetConsentFormCommuniqueByTokenToken(
  token?: string,
  enabled: boolean = true,
): UseQueryResult<ConsentFormsCommuniqueDTO, Error> {
  return useQuery<ConsentFormsCommuniqueDTO, Error>({
    queryKey: ['consentFormCommuniqueByToken', token],
    queryFn: () => getApiCommunicationGetConsentFormCommuniqueByTokenToken(token ?? ''),
    enabled: enabled && !isEmptyString(token),
  });
}

export function useFormWorkflowRenderingMetadata(
  formWorkflowId?: number,
  enabled: boolean = true,
): UseQueryResult<FormsRenderingMetadataResponseDTO, Error> {
  return useQuery<FormsRenderingMetadataResponseDTO, Error>({
    queryKey: ['formWorkflowRenderingMetadata', formWorkflowId],
    queryFn: () => getApiFormWorkflowGetRenderingMetadataFormWorkflowIdAssignee(
      formWorkflowId ?? 0,
      FormFieldAssignee.Patient,
    ),
    enabled: enabled && !isNil(formWorkflowId),
  });
}

export function useGetFormWorkflowPdfs(
  formWorkflowId?: number,
  enabled: boolean = true,
): UseQueryResult<MultiMediaResponseDTO, Error> {
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['formWorkflowPdfs', formWorkflowId],
    queryFn: () => getApiFormWorkflowGetWorkflowPdfsFormWorkflowId(formWorkflowId ?? 0),
    enabled: enabled && !isNil(formWorkflowId),
  });
}

export function useCompleteFormWorkflowPatientStep(): UseMutationResult<
  FormWorkflowWithTransitionSuccessMessageResponseDTO,
  Error,
  FormWorkflowCompletionRequestDTO
  > {
  return useMutation<
    FormWorkflowWithTransitionSuccessMessageResponseDTO,
    Error,
    FormWorkflowCompletionRequestDTO>({
      mutationFn: (payload) => postApiFormWorkflowCompletePatientStep(payload),
    });
}

export function UseMessageCommuniqueByToken(): UseMutationResult<
PatientInfoCommuniqueDTO, Error, string> {
  return useMutation<PatientInfoCommuniqueDTO, Error, string>({
    mutationFn: (token) => getApiCommunicationGetPatientInfoCommuniqueByTokenToken(token),
  });
}

export function UseGetOrganizationByPatientId(): UseMutationResult<
OrganizationDTO, Error, GetApiOrganizationGetOrgByPatientIdParams> {
  return useMutation<OrganizationDTO, Error, GetApiOrganizationGetOrgByPatientIdParams>({
    mutationFn: (params) => getApiOrganizationGetOrgByPatientId(params),
  });
}

export function UseVerifyPatientDOBById(): UseMutationResult<
 None | void, Error, PatientDOBDTO> {
  return useMutation<None | void, Error, PatientDOBDTO>({
    mutationFn: (params) => postApiPatientVerifyPatientDOBById(params),
  });
}

export function useAddReasonForVisit(): UseMutationResult<
  PatientDTO,
  Error,
  unknown
  > {
  return useMutation<PatientDTO, Error, unknown>({
    mutationFn: (payload) => postApiPatientAddReasonForVisit(payload as ReasonForVisitDTO),
  });
}

export function useAddPatientMedicalHistory(): UseMutationResult<
  PatientDTO,
  Error,
  unknown
  > {
  return useMutation<PatientDTO, Error, unknown>({
    mutationFn: (payload) => postApiPatientAddPatientMedicalHistory(payload as any),
  });
}

export function useAddPatientPharmacyAllergyAndMedication(): UseMutationResult<
  ISyncPatientResult,
  Error,
  unknown
  > {
  return useMutation<ISyncPatientResult, Error, unknown>({
    mutationFn: (
      payload,
    ) => postApiPatientAddPatientPharmacyAllergyAndMedication(
      payload as PatientPharmacyAllergyMedicationRequestDTO,
    ),
  });
}

export function useAddPatientInsurance(): UseMutationResult<
  PatientDTO,
  Error,
  PatientInsurancesRequestDTO
  > {
  return useMutation<PatientDTO, Error, PatientInsurancesRequestDTO>({
    mutationFn: (payload) => postApiPatientAddPatientInsurances(payload),
  });
}

export function useGetInsuranceAttachments(
  insuranceId?: number,
  enabled: boolean = true,
): UseQueryResult<InsuranceAttachmentDTO[], Error> {
  return useQuery<InsuranceAttachmentDTO[], Error>({
    queryKey: ['insuranceAttachments', insuranceId],
    queryFn: () => getApiPatientGetInsuranceAttachmentsInsuranceId(insuranceId as number),
    enabled: enabled && !isNil(insuranceId),
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

export function useGetUnassociatedInsuranceCards(
  patientId?: number,
  enabled: boolean = true,
): UseQueryResult<MultiMediaResponseDTO, Error> {
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['unassociatedInsuranceCards', patientId],
    queryFn: () => getApiPatientGetUnassociatedInsuranceCardsPatientId(patientId as number),
    enabled: enabled && !isNil(patientId),
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

export function useSyncInsuranceAttachments(): UseMutationResult<
  InsuranceAttachmentDTO[],
  Error,
  SyncInsuranceAttachmentsRequestDTO
  > {
  return useMutation<InsuranceAttachmentDTO[], Error, SyncInsuranceAttachmentsRequestDTO>({
    mutationFn: (payload) => postApiPatientSyncInsuranceAttachments(payload),
  });
}

export function useUploadAndAssociateInsuranceCard(): UseMutationResult<
  InsuranceAttachmentDTO,
  Error,
  UploadAndAssociateInsuranceCardRequestDTO
  > {
  return useMutation<
  InsuranceAttachmentDTO,
  Error,
  UploadAndAssociateInsuranceCardRequestDTO
  >({
    mutationFn: (payload) => postApiPatientUploadAndAssociateInsuranceCardWithInsurance(payload),
  });
}

export function useUploadImageOrDocuments(): UseMutationResult<
  PatientMultiMediaResponseDTO,
  Error,
  {
    patientId: number;
    multimediaType: string;
    dataURL: string;
    fileName: string;
  }
  > {
  return useMutation<
  PatientMultiMediaResponseDTO,
  Error,
  { patientId: number; multimediaType: string; dataURL: string; fileName: string }
  >({
    mutationFn: async ({
      patientId,
      multimediaType,
      dataURL,
      fileName,
    }) => {
      const ext = dataURL.split(';')[0].split('/')[1];
      const nameOfFile = `${fileName}.${ext}`;

      const payload: PatientMultiMediaUploadDTO = {
        patientId,
        multimediaType,
        dataUrl: dataURL,
        fileName: nameOfFile,
      };

      return postApiMultimediaPostPatientMultiMedia(payload);
    },
  });
}

export function useDeleteMultiMedia(): UseMutationResult<
  None,
  Error,
  MultiMediaDeleteDTO
  > {
  return useMutation<None, Error, MultiMediaDeleteDTO>({
    mutationFn: (payload) => postApiMultimediaDeleteMultiMedia(payload),
  });
}

export function useGetInvoiceSummary(): UseMutationResult<
  SquareInvoiceSummaryResponseDTO,
  Error,
  number
  > {
  return useMutation<SquareInvoiceSummaryResponseDTO, Error, number>({
    mutationFn: async (squareInvoiceId) => {
      const response = await getApiBillingGetInvoiceSummary({ squareInvoiceId });
      return {
        totalBalance: response?.totalBalance ?? 0,
        paymentLink: response?.paymentLink ?? null,
        status: response?.status ?? '',
        paidAt: response?.paidAt ?? '',
      };
    },
  });
}

export function useGetGeoCodeByAddress(): UseMutationResult<
  AddressDTO,
  Error,
  string
  > {
  return useMutation<AddressDTO, Error, string>({
    mutationFn: async (address) => {
      const response = await getApiAddressGetGeoCodeByAddress({ address });
      return {
        unit: response?.unit ?? null,
        street: response?.street ?? null,
        city: response?.city ?? null,
        state: response?.state ?? null,
        zipCode: response?.zipCode ?? null,
        shortZipCode: response?.shortZipCode ?? null,
        country: response?.country ?? null,
        formattedAddresses: response?.formattedAddresses,
      };
    },
  });
}

export function useGetAppointmentDetailsByAppointmentId(): UseMutationResult<
AppointmentDetailsDTO,
  Error,
  number
  > {
  return useMutation<AppointmentDetailsDTO, Error, number>({
    mutationFn: (
      appointmentId,
    ) => getApiEncounterGetAppointmentDetailsByAppointmentIdAppointmentId(appointmentId),
  });
}

export function useGetAgreementFormsForPatient(): UseMutationResult<
FormsResponseDTO,
  Error,
  number
  > {
  return useMutation<FormsResponseDTO, Error, number>({
    mutationFn: (
      patientId,
    ) => getApiPatientGetAgreementFormsForPatientPatientId(patientId),
  });
}

export function useGetSymptomsFromImmediatePastVisit(): UseMutationResult<
  PatientDTO,
  Error,
  number
  > {
  return useMutation<PatientDTO, Error, number>({
    mutationFn: async (patientId) => {
      const response = await getApiPatientGetSymptomsFromImmediatePastVisitPatientId(patientId);
      return response as PatientDTO;
    },
  });
}

export function useSubmitDosespotConsentForm(): UseMutationResult<
  None | void,
  Error,
  DosespotConsentByPatientDTO
  > {
  return useMutation<None | void, Error, DosespotConsentByPatientDTO>({
    mutationFn: (payload) => postApiPatientSubmitDosespotConsentForm(payload),
  });
}

export function useGetPatientByIdWithPharmacy(): UseMutationResult<
  PatientDTO,
  Error,
  number
  > {
  return useMutation<PatientDTO, Error, number>({
    mutationFn: (
      patientId,
    ) => getApiPatientGetPatientByIdWithPharmacyMedicationAndAllergyId(patientId),
  });
}

export function useERxPharmacySearch(): UseMutationResult<
  ERxPharmacySearchResponseDTO,
  Error,
  GetApiERxPharmacySearchParams
  > {
  return useMutation<ERxPharmacySearchResponseDTO, Error, GetApiERxPharmacySearchParams>({
    mutationFn: (params) => getApiERxPharmacySearch(params),
  });
}

export function useERxMedicationSearch(): UseMutationResult<
  ERxMedicationSearchResultDTO[],
  Error,
  GetApiERxMedicationSearchParams
  > {
  return useMutation<ERxMedicationSearchResultDTO[], Error, GetApiERxMedicationSearchParams>({
    mutationFn: async (params) => {
      const response = await getApiERxMedicationSearch(params);
      return response?.items ?? [];
    },
  });
}

export function useERxDrugAllergySearch(): UseMutationResult<
  ERxDrugAllergySearchResultDTO[],
  Error,
  GetApiERxDrugAllergySearchParams
  > {
  return useMutation<ERxDrugAllergySearchResultDTO[], Error, GetApiERxDrugAllergySearchParams>({
    mutationFn: async (params) => {
      const response = await getApiERxDrugAllergySearch(params);
      return response?.items ?? [];
    },
  });
}

export function GetPatientMultimediaByTypePatientIdMultimediaType({
  patientId,
  multimediaType,
}: {
  patientId: number;
  multimediaType: MultimediaType;
}) {
  return useQuery({
    queryKey: ['patientMultiMedia', patientId, multimediaType],
    queryFn: async () => {
      const response = await getApiMultimediaGetPatientMultimediaByTypePatientIdMultimediaType(
        patientId,
        multimediaType,
      );
      return response?.list ?? [];
    },
    enabled: !!patientId,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });
}

export function CallForAppointmentReminder(): UseMutationResult<
  AppointmentReminderCallResponseDTO,
  Error,
  PostApiAppointmentReminderAgentCallForAppointmentReminderParams> {
  return useMutation<AppointmentReminderCallResponseDTO,
    Error,
    PostApiAppointmentReminderAgentCallForAppointmentReminderParams>({
      mutationFn: (params) => postApiAppointmentReminderAgentCallForAppointmentReminder(params),
    });
}

export function useGetOrgByIpadBaseIntakeUrl(): UseMutationResult<
 OrganizationByIntakeResponseDTO, Error, OrganizationByIntakeRequestDTO> {
  return useMutation<OrganizationByIntakeResponseDTO, Error, OrganizationByIntakeRequestDTO>({
    mutationFn: (params) => postApiOrganizationGetOrgByIpadBaseIntakeUrl(params),
  });
}

export function useGetPatientMultimediaByMultiMediaId(): UseMutationResult<
 MultiMediaResponseDTO, Error, number> {
  return useMutation<MultiMediaResponseDTO, Error, number>({
    mutationFn: (mediaId) => getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId(mediaId),
  });
}

export function useGetBillingMultiMediaByIdForPatient(): UseMutationResult<
 MultiMediaResponseDTO, Error, number> {
  return useMutation<MultiMediaResponseDTO, Error, number>({
    mutationFn: (
      mediaId,
    ) => getApiMultimediaGetBillingMultiMediaByIdForPatientBillingMultiMediaId(mediaId),
  });
}

export function useGetFavoriteInsurancesByOrg(
  params: GetApiCHGetFavoriteInsurancesByOrgParams,
): UseQueryResult<PayerEntryDTO[], Error> {
  return useQuery<PayerEntryDTO[], Error>({
    queryKey: ['favoriteInsurancesByOrg'],
    queryFn: () => getApiCHGetFavoriteInsurancesByOrg(params),
  });
}

export function useGetFavoriteInsurances(orgId: number) {
  const {
    data,
    isLoading,
    error,
  } = useGetFavoriteInsurancesByOrg({ organizationId: orgId });

  return {
    isLoading,
    hasError: !!error,
    data: compact(data ?? []),
  };
}

export function useGetPCCItemsByPatientId(): UseMutationResult<
  PatientPCCItemsDTO, Error, number> {
  return useMutation<PatientPCCItemsDTO, Error, number>({
    mutationFn: (patientId: number) => getApiPatientGetPCCItemsByPatientIdPatientId(patientId),
  });
}
